import React from "react";
import { Link } from "react-router-dom";
import {
  Home as HomeIcon, Sun, DoorOpen, Trees, Wind, Waves, ArrowRight,
  Sparkles,
  FileCheck, Layers, RefreshCw,

  Droplets,
  ShieldCheck, Award, Handshake, Clock,
} from "lucide-react";
import SEO from "../components/SEO";
import { services, remodeling } from "../data/site";

const iconMap = { Home: HomeIcon, Sun, Sparkles, FileCheck, Layers, RefreshCw, Droplets, ShieldCheck, Award, Handshake, Clock };

// `badge` is an optional mark shown above the eyebrow — used by the warranty
// claim page for the workmanship seal. Pages that pass nothing are unchanged.
const PageHero = ({ eyebrow, title, subtitle, image, imageAlt, badge }) => (
  <section className="relative pt-40 pb-20 section-dark text-white overflow-hidden">
    {image && (
      <div className="absolute inset-0">
        <img fetchPriority="high" decoding="async" src={image} alt={imageAlt || `${title} — Pepla Roofing, Phoenix AZ`} className="w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 to-ink" />
      </div>
    )}
    <div className="relative max-w-7xl mx-auto px-5 lg:px-8 text-center">
      {badge && <div className="flex justify-center mb-7">{badge}</div>}
      <span className="kicker-dark">{eyebrow}</span>
      <h1 className="font-heading text-4xl md:text-6xl uppercase tracking-tight mt-3 mb-4">{title}</h1>
      <div className="brand-line mx-auto mb-5" />
      {subtitle && <p className="text-white/70 max-w-2xl mx-auto text-lg">{subtitle}</p>}
    </div>
  </section>
);

export { PageHero };

const Services = () => {
  return (
    <div>
      <SEO
        title="Our Services | Roof Repair, Replacement & Inspections"
        description="Residential roof repair and replacement, tile underlayment, foam and flat roofs, and free roof inspections for homes across Phoenix and Maricopa County, AZ."
      />
      <PageHero
        eyebrow="What We Do"
        title="Our Services"
        subtitle="Tile, shingle, flat and foam roofs on Valley homes — repaired, replaced and inspected by one crew that tells you what yours actually needs."
      />

      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 space-y-16">
          {services.map((s, i) => {
            const Icon = iconMap[s.icon];
            return (
              <div key={s.slug} className={`grid lg:grid-cols-2 gap-10 items-center ${i % 2 === 1 ? "lg:[direction:rtl]" : ""}`}>
                <div className="img-zoom rounded-2xl overflow-hidden shadow-xl [direction:ltr]">
                  <img loading="lazy" decoding="async" src={s.hero} alt={s.name} className="w-full h-72 md:h-96 object-cover" />
                </div>
                <div className="[direction:ltr]">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="w-12 h-12 rounded-xl bg-ink flex items-center justify-center"><Icon size={24} className="text-brand" /></span>
                    <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-tight">{s.name}</h2>
                  </div>
                  <p className="text-muted-foreground leading-relaxed mb-6">{s.intro}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-8">
                    {s.features.slice(0, 6).map((f) => (
                      <div key={f} className="flex items-center gap-2 text-sm">
                        <ArrowRight size={14} className="text-brand shrink-0" /> {f}
                      </div>
                    ))}
                  </div>
                  <Link
                    to={`/services/${s.slug}`}
                    className="inline-flex items-center gap-2 bg-brand hover:bg-brand-light text-ink font-semibold uppercase tracking-wider text-sm px-6 py-3 rounded-md transition-colors"
                  >
                    View {s.name} <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Remodeling lives on its own page rather than in the services list,
          because it is a different licence classification and a different
          sale. This is how someone browsing "All Services" finds it. */}
      {remodeling.enabled && (
        <section className="pb-24 bg-background">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <div className="bg-ink text-white rounded-2xl p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-8">
              <div className="flex-1">
                <span className="block text-brand text-xs font-semibold tracking-[0.22em] uppercase mb-3">
                  Beyond the roof
                </span>
                <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-tight leading-[1.05] mb-4">
                  Home Remodeling
                </h2>
                <p className="text-white/70 leading-relaxed max-w-xl">
                  Room additions, patio covers, windows, stucco, kitchens and bathrooms — the same
                  written scope and named products we put behind a roof, applied to the rest of
                  the house.
                </p>
              </div>
              <Link
                to="/remodeling"
                className="inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-light text-ink font-semibold uppercase tracking-wider text-sm px-7 py-4 rounded-md transition-colors shrink-0 self-start md:self-auto"
              >
                See remodeling <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>
      )}

      <section className="py-20 section-dark text-white text-center">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mb-4">Not Sure Where To Start?</h2>
          <p className="text-white/70 mb-8">Our team will assess your project and recommend the right solution — free of charge.</p>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-brand hover:bg-brand-light text-ink font-semibold uppercase tracking-wider text-sm px-8 py-4 rounded-md transition-colors">
            Get a Free Estimate <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
